import type { RefObject } from 'react';
import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { styled } from '@compiled/react';

import ImageIcon from '@atlaskit/icon/core/image';
import { token } from '@atlaskit/tokens';
// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import { Popup } from '@atlaskit/popup/popup';
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-atlaskit-theme
import { layers } from '@atlaskit/theme/constants';

import type { UnsplashResult } from '@atlassian/company-hub-common/media-components/unsplash/types';
import type { GenerateImageProps } from '@atlassian/ai-image-generation/types';

import type { COVERPICTUREWIDTH } from './contentTopperConstants.ts';
import { UnsplashPopupMenuContent } from './UnsplashPopupMenuContent.tsx';

export type UnsplashDropdownMenuProps = {
	imageSrc?: string;
	imageAltText?: string;
	initialUnsplashInfoArray: UnsplashResult[] | undefined;
	closePopup: () => void;
	openMediaBrowser?: () => void;
	coverPictureState: any;
	setIsUploadingPicture: (isUploadingPicture: boolean) => void;
	handleUnsplashUpload: (url: string) => void;
	floatingToolbarButtonStyle?: object;
	userOffline?: boolean;
	isModalOpenState: boolean;
	onDelete?: () => void;
	onReposition?: () => void;
	dropdownRef?: RefObject<HTMLElement | null>;
	dropdownPopupComponentRef?: RefObject<HTMLElement | null>;
	contentId: string;
	onImageWidthChange?: (coverPictureWidth: COVERPICTUREWIDTH) => void;
	onCoverImageWidthDropdownOptionSelected?: (isCoverImageWidthChanged: boolean) => void;
	generateAIHeaderImage?: (props: GenerateImageProps) => Promise<string | undefined>;
};

const i18n = defineMessages({
	headerLabel: {
		id: 'content-topper.add.header.image.message.floating-toolbar',
		description:
			'Message displayed on the button which allows the user to change the header image in a page in the floating toolbar.',
		defaultMessage: 'Header image',
	},
	updateImage: {
		id: 'content-topper.page-title.popup.update.header.image',
		description: 'Message user can click on to update/change their header image.',
		defaultMessage: 'Update header image',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DropdownTrigger = styled.button<{ userOffline?: boolean }>({
	display: 'flex',
	flexFlow: 'column nowrap',
	alignItems: 'center',
	color: token('color.background.input'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	cursor: ({ userOffline }) => (userOffline ? 'not-allowed' : 'pointer'),
	background: 'none',
	border: 'none',
	outlineColor: token('color.border.information'),
});

export const UnsplashDropdownMenu = ({
	isModalOpenState,
	closePopup,
	initialUnsplashInfoArray,
	openMediaBrowser,
	userOffline,
	handleUnsplashUpload,
	setIsUploadingPicture,
	coverPictureState,
	floatingToolbarButtonStyle,
	onReposition,
	onDelete,
	imageSrc,
	dropdownRef,
	dropdownPopupComponentRef,
	contentId,
	onImageWidthChange,
	onCoverImageWidthDropdownOptionSelected,
	generateAIHeaderImage,
}: UnsplashDropdownMenuProps): React.JSX.Element => {
	return (
		<Popup
			isOpen={isModalOpenState}
			onClose={closePopup}
			placement="bottom"
			zIndex={layers.dialog()}
			strategy="absolute"
			shouldRenderToParent
			role="dialog"
			trigger={(triggerProps) => (
				<DropdownTrigger
					data-testid="update-image-button"
					{...triggerProps}
					tabIndex={0}
					aria-expanded={isModalOpenState}
					ref={triggerProps.ref as React.RefObject<HTMLButtonElement>}
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
					style={floatingToolbarButtonStyle || {}}
					userOffline={userOffline}
					disabled={userOffline}
				>
					<ImageIcon label="" color={token('color.icon.inverse')} />
					<FormattedMessage {...i18n.updateImage} />
				</DropdownTrigger>
			)}
			content={() => (
				<UnsplashPopupMenuContent
					dropdownRef={dropdownRef}
					contentId={contentId}
					imageSrc={imageSrc}
					initialUnsplashInfoArray={initialUnsplashInfoArray}
					openMediaBrowser={openMediaBrowser}
					handleUnsplashUpload={handleUnsplashUpload}
					setIsUploadingPicture={setIsUploadingPicture}
					coverPictureState={coverPictureState}
					closePopup={closePopup}
					onReposition={onReposition}
					onDelete={onDelete}
					placement="bottom"
					userOffline={userOffline}
					dropdownPopupComponentRef={dropdownPopupComponentRef}
					onImageWidthChange={onImageWidthChange}
					isPageTitleComponent
					onCoverImageWidthDropdownOptionSelected={onCoverImageWidthDropdownOptionSelected}
					generateAIHeaderImage={generateAIHeaderImage}
				/>
			)}
		/>
	);
};
